import type { SupportedLanguages } from "@shared/i18n"
import { getCountryCodeFromLocale } from "@shared/i18n"
import { useTranslation } from "react-i18next"

import type { FilterOption } from "../controlled-filter-form/model/types"
import { ControlledSelect } from "../controlled-filter-form/ui/controlled-select"
import { FlagIcon } from "../country-pill"

interface LanguageSelectProps {
	value: SupportedLanguages | ""
	values: SupportedLanguages[]
	onChange: (value: SupportedLanguages | "") => void
	label?: string
	name?: string
	className?: string
	dropdownClassName?: string
	includeEmptyOption?: boolean
	emptyOptionLabel?: string
	"data-cy"?: string
	isDisabled?: boolean
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
	value,
	values,
	onChange,
	label = "",
	name = "language",
	className = "w-full",
	dropdownClassName,
	includeEmptyOption = false,
	emptyOptionLabel,
	"data-cy": dataCy = "language-select",
	isDisabled = false,
}) => {
	const { t } = useTranslation("shared", { keyPrefix: "languages" })

	const languageOptions: FilterOption[] = [
		...(includeEmptyOption
			? [{ label: emptyOptionLabel ?? "", value: "" }]
			: []),
		...values.map(lang => ({
			label: t(lang),
			value: lang,
			customIcon: (
				<FlagIcon
					className="max-w-4 max-h-3"
					code={getCountryCodeFromLocale(lang)}
				/>
			),
		})),
	]

	return (
		<ControlledSelect
			className={className}
			dropdownClassName={dropdownClassName}
			label={label}
			name={name}
			value={value}
			onChange={value => onChange(value as SupportedLanguages | "")}
			options={languageOptions}
			searchable={false}
			disabled={isDisabled}
			data-cy={dataCy}
		/>
	)
}
